import { useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import { Input } from "@/components/base/Field";
import { useToast } from "@/components/base/Toast";
import { recordReminders, type UnpaidRow } from "@/services/reminderService";
import type { SchoolInfo } from "@/types";
import { exportPaymentNoticesPdf } from "@/utils/paymentNoticePdf";
import { formatMoney } from "@/utils/format";

interface Props {
  /** Élèves dont on imprime l'avis (null = fenêtre fermée). */
  rows: UnpaidRow[] | null;
  school: SchoolInfo;
  /** Libellé du lot imprimé (« CM2 », « 12 élèves sélectionnés »...). */
  scopeLabel: string;
  /** Enregistrer chaque avis comme une relance (permission reminders:create). */
  canRecord: boolean;
  onClose: () => void;
  onPrinted: () => void;
}

const inDays = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

/** Impression en masse des avis de paiement (3 par page A4), avec la date limite de régularisation. */
export default function NoticePrintModal({ rows, school, scopeLabel, canRecord, onClose, onPrinted }: Props) {
  const { notify } = useToast();
  const [deadline, setDeadline] = useState(inDays(7));
  const [printing, setPrinting] = useState(false);
  const total = (rows ?? []).reduce((acc, r) => acc + r.lines.reduce((s, l) => s + l.remaining, 0), 0);

  const print = async () => {
    if (!rows?.length) return;
    if (!deadline) {
      notify("Choisissez la date limite de régularisation.", "error");
      return;
    }
    setPrinting(true);
    try {
      const name = scopeLabel.replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-|-$/g, "") || "eleves";
      await exportPaymentNoticesPdf(rows, school, deadline, `avis-paiement-${name}.pdf`);
      if (canRecord) {
        await recordReminders(
          rows.map((r) => r.enrollmentId),
          "PAPIER",
          deadline,
        );
      }
      notify(`${rows.length} avis de paiement générés${canRecord ? " et enregistrés comme relances" : ""}.`);
      onPrinted();
      onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Impression impossible.", "error");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Modal
      open={rows !== null}
      onClose={onClose}
      title="Imprimer les avis de paiement"
      subtitle={scopeLabel}
      icon="ri-printer-line"
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button icon="ri-file-pdf-2-line" onClick={print} disabled={printing || !rows?.length}>
            {printing ? "Préparation..." : "Générer le PDF"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-md border border-background-200 bg-background-100/60 px-3 py-2.5">
            <p className="text-[11px] uppercase tracking-wide text-foreground-400">Avis</p>
            <p className="text-lg font-semibold text-foreground-900">{rows?.length ?? 0}</p>
          </div>
          <div className="rounded-md border border-background-200 bg-background-100/60 px-3 py-2.5">
            <p className="text-[11px] uppercase tracking-wide text-foreground-400">Montant réclamé</p>
            <p className="text-lg font-semibold text-accent-700">{formatMoney(total)}</p>
          </div>
        </div>
        <Input
          label="À régulariser avant le"
          type="date"
          value={deadline}
          min={inDays(0)}
          onChange={(e) => setDeadline(e.target.value)}
          required
        />
        <p className="text-xs text-foreground-500">
          3 avis par page A4, séparés par un trait de découpe. Chaque avis liste les frais dus de l'élève, leur
          échéance et le total.
          {canRecord && " Chaque avis est noté dans l'historique des relances de l'élève."}
        </p>
      </div>
    </Modal>
  );
}
